import { LoadingButton } from '@mui/lab';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import { IContactListItem } from 'src/services/contacts/contacts.types';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import ContactsNotesModal from 'src/components/custom-tables/contacts-table/contacts-notes-modal';

// ----------------------------------------------------------------------

interface ListContactRowActionsProps {
  row: IContactListItem;
  onView: (contactId: string) => void;
  onRemoveFromList: (row: IContactListItem) => void;
  isRemoving?: boolean;
}

/**
 * Row kebab for a contact inside a saved list: View, Notes, and Remove from list.
 *
 * Deliberately NOT ContactRowActions, whose Remove deletes the contact outright. Here the
 * destructive action only severs this list membership and the contact survives everywhere else —
 * sharing one component would put two very different consequences behind the same menu item.
 */
export default function ListContactRowActions({ row, onView, onRemoveFromList, isRemoving }: ListContactRowActionsProps) {
  const { t } = useTranslate();
  const popover = usePopover();

  const notes = useBoolean();
  const confirm = useBoolean();

  return (
    <>
      <IconButton color={popover.open ? 'inherit' : 'default'} onClick={popover.onOpen}>
        <Iconify icon="eva:more-horizontal-fill" />
      </IconButton>

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow="right-top" sx={{ width: 200 }}>
        <MenuItem onClick={() => { popover.onClose(); onView(row.id); }}>
          <Iconify icon="solar:eye-bold" />
          <Typography variant="body2">{t('common.view')}</Typography>
        </MenuItem>

        {row.thread_id && (
          <MenuItem onClick={() => { popover.onClose(); notes.onTrue(); }}>
            <Iconify icon="mdi:comments" />
            <Typography variant="body2">{t('common.notes')}</Typography>
          </MenuItem>
        )}

        <MenuItem sx={{ color: 'error.main' }} onClick={() => { popover.onClose(); confirm.onTrue(); }}>
          <Iconify icon="ix:remove-circle-filled" />
          <Typography variant="body2">{t('lists.actions.remove_from_list')}</Typography>
        </MenuItem>
      </CustomPopover>

      {row.thread_id && (
        <ContactsNotesModal threadId={row.thread_id} open={notes.value} onClose={notes.onFalse} />
      )}

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title={t('lists.actions.remove_from_list')}
        content={t('lists.actions.remove_from_list_confirm', { count: 1 })}
        action={
          <LoadingButton
            loading={isRemoving}
            variant="contained"
            color="error"
            onClick={() => { onRemoveFromList(row); confirm.onFalse(); }}
          >
            {t('common.remove')}
          </LoadingButton>
        }
      />
    </>
  );
}
